import CalendarEntry from 'owa-service/lib/contract/CalendarEntry';
import CalendarFolderTypeEnum from 'owa-service/lib/contract/CalendarFolderTypeEnum';
import ChangeNotificationStatus from 'owa-service/lib/contract/ChangeNotificationStatus';
import GetCalendarFoldersResponse from 'owa-service/lib/contract/GetCalendarFoldersResponse';
import LocalCalendarEntry from 'owa-service/lib/contract/LocalCalendarEntry';
import SharedCalendarViewState from '../data/store/schema/SharedCalendarViewState';
import SharedCalendarsOptionViewState from '../data/store/schema/SharedCalendarsOptionViewState';
import owaCalendarStrings from 'owa-strings/owa-calendar-strings';
import { getCalendarSharingHexColor } from 'owa-calendar-sharing/lib/utils/getCalendarSharingHexColor';

const assign = require('object-assign');

/**
 * Utility function to parse calendar folders and calendar groups
 * @returns state of type SharedCalendarsOptionViewState
 */
export default function parseCalendarFoldersResponse(
    calendarFoldersResponse: GetCalendarFoldersResponse
): SharedCalendarsOptionViewState {
    let folderIdList: string[] = [];
    let folderIdListPublished: string[] = [];
    let folderIdCalendarViewStateMap: { [folderId: string]: SharedCalendarViewState } = {};
    let initialNotificationState: { [folderId: string]: ChangeNotificationStatus } = {};
    let calendarGroups = calendarFoldersResponse.CalendarGroups;

    for (let i = 0; i < calendarGroups.length; i++) {
        let calendars: LocalCalendarEntry[] = calendarGroups[i].Calendars;
        for (let j = 0; j < calendars.length; j++) {
            let calendarEntry: LocalCalendarEntry = calendars[j];
            let publishedCal = true;

            if (!calendarEntry.CalendarFolderId) {
                continue;
            }

            if (
                calendarEntry.IsReadOnly ||
                calendarEntry.IsGroupMailboxCalendar ||
                // Note that there's a bug in jsMVVM that does not filter out calendars that you can't share, so the
                // list of publishable calendars will be different between the two clients
                !calendarEntry.CanShare ||
                calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.HolidayCalendar
            ) {
                publishedCal = false;
            }

            let folderId = calendarEntry.CalendarFolderId.Id;

            folderIdList.push(folderId);

            if (publishedCal) {
                folderIdListPublished.push(folderId);
            }

            folderIdCalendarViewStateMap[folderId] = {
                folderId: folderId,
                calendarName: getCalendarDisplayName(calendarEntry),
                hexColorString: calendarEntry.CanShare
                    ? getCalendarSharingHexColor(calendarEntry)
                    : undefined,
                canShare: calendarEntry.CanShare,
                canEnableChangeNotifications: false,
            };
        }
    }

    let calendarFolders = calendarFoldersResponse.CalendarFolders;
    for (let i = 0; i < calendarFolders.length; i++) {
        let calendarFolder = calendarFolders[i];
        let folderId: string = calendarFolder.FolderId.Id;
        if (calendarFolder.CanEnableChangeNotifications) {
            folderIdCalendarViewStateMap[folderId].canEnableChangeNotifications = true;
            initialNotificationState[folderId] = calendarFolder.ChangeNotificationStatus;
        }
    }

    let calendars: SharedCalendarViewState[] = [];
    for (let i = 0; i < folderIdList.length; i++) {
        calendars.push(folderIdCalendarViewStateMap[folderIdList[i]]);
    }

    let calendarsPublished: SharedCalendarViewState[] = [];
    for (let i = 0; i < folderIdListPublished.length; i++) {
        calendarsPublished.push(folderIdCalendarViewStateMap[folderIdListPublished[i]]);
    }

    return {
        calendars: calendars,
        //Creating new objects to prevent mutating the same object for both initial and current state.
        initialState: assign({}, initialNotificationState),
        currentState: assign({}, initialNotificationState),
        calendarsPublish: calendarsPublished,
    } as SharedCalendarsOptionViewState;
}

function getCalendarDisplayName(calendarEntry: CalendarEntry): string {
    return calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.DefaultCalendar
        ? owaCalendarStrings.calendarText
        : calendarEntry.CalendarName;
}
